import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { InAppNotification } from '../models/NotificationModels';
import { 
  subscribeToNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  deleteNotification,
  syncRecentPostMentions
} from '../services/notificationCenterService';

export function useNotifications() {
  const { user, profile } = useAuth();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Retroactively sync mentions for recent feed posts
    syncRecentPostMentions({
      uid: user.uid,
      displayName: profile?.displayName || profile?.nickname || user.displayName || 'Member',
      photoUrl: profile?.photoUrl || user.photoURL || '',
    }).catch((err) => {
      console.warn('[useNotifications] Mention sync failed:', err);
    });

    const unsubscribe = subscribeToNotifications(
      user.uid,
      (items) => {
        setNotifications(items);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, profile?.displayName, profile?.photoUrl]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.isRead).length;
  }, [notifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.uid) return;
    // Optimistic UI update
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    );
    await markNotificationAsRead(user.uid, notificationId);
  }, [user?.uid]);

  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;
    const unreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);
    if (unreadIds.length === 0) return;

    // Optimistic UI update
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    await markAllNotificationsAsRead(user.uid, unreadIds);
  }, [user?.uid, notifications]);

  const removeNotification = useCallback(async (notificationId: string) => {
    if (!user?.uid) return;
    // Optimistic UI update
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    await deleteNotification(user.uid, notificationId);
  }, [user?.uid]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification: removeNotification,
  };
}
